'use client';

import Link from 'next/link';
import {
  ArrowLeft, LayoutDashboard, FileSearch, BarChart3, Keyboard,
  PanelLeftClose, PanelLeftOpen, type LucideIcon,
} from 'lucide-react';

export type TerminalViewType = 'dashboard' | 'research' | 'market-data';

interface TerminalNavProps {
  active: TerminalViewType;
  /** Symbol the Research entry targets (current research symbol or watchlist selection) */
  symbol: string;
  onNavigate: (view: TerminalViewType) => void;
  onShowHelp: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const NAV_ITEMS: { view: TerminalViewType; label: string; icon: LucideIcon; shortcut: string }[] = [
  { view: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, shortcut: 'g d' },
  { view: 'research', label: 'Research', icon: FileSearch, shortcut: 'g r' },
  { view: 'market-data', label: 'Market Data', icon: BarChart3, shortcut: 'g m' },
];

const itemClass = (active: boolean) =>
  `w-full flex flex-col items-center gap-1 px-1 py-2.5 border-l-2 transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[#1A2035] text-[var(--color-accent)]'
      : 'border-transparent text-[#6B7280] hover:text-[#E5E7EB] hover:bg-[#1A2035]/50'
  }`;

function HomeLink() {
  return (
    <Link
      href="/"
      title="Back to EquityLabs"
      aria-label="Back to EquityLabs"
      className="h-12 flex items-center justify-center border-b border-[#1F2937] text-[#6B7280] hover:text-[var(--color-accent)] transition-colors flex-shrink-0"
    >
      <ArrowLeft className="w-4 h-4" />
    </Link>
  );
}

export function TerminalNav({ active, symbol, onNavigate, onShowHelp, collapsed, onToggleCollapsed }: TerminalNavProps) {
  if (collapsed) {
    return (
      <nav
        aria-label="Terminal navigation"
        className="w-7 flex-shrink-0 bg-[#0D1117] border-r border-[#1F2937] flex flex-col"
      >
        <HomeLink />
        <button
          onClick={onToggleCollapsed}
          title="Expand navigation"
          aria-label="Expand navigation"
          aria-expanded="false"
          className="h-12 flex items-center justify-center border-b border-[#1F2937] text-[#6B7280] hover:text-[var(--color-accent)] transition-colors"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Terminal navigation"
      className="w-14 flex-shrink-0 bg-[#0D1117] border-r border-[#1F2937] flex flex-col"
    >
      <HomeLink />

      {NAV_ITEMS.map(({ view, label, icon: Icon, shortcut }) => {
        const name = view === 'research' ? `${label} ${symbol}` : label;
        return (
          <button
            key={view}
            onClick={() => onNavigate(view)}
            title={`${name} (${shortcut})`}
            aria-label={name}
            aria-current={active === view ? 'page' : undefined}
            className={itemClass(active === view)}
          >
            <Icon className="w-4 h-4" />
            <span className="text-[8px] font-mono uppercase leading-tight text-center">{label}</span>
            {view === 'research' && (
              <span className="text-[8px] font-mono font-semibold leading-none text-[var(--color-accent)]">{symbol}</span>
            )}
          </button>
        );
      })}

      <div className="mt-auto flex flex-col">
        {/* Keyboard shortcuts are pointless on touch — desktop only */}
        <button
          onClick={onShowHelp}
          title="Keyboard shortcuts (?)"
          aria-label="Show keyboard shortcuts"
          className="hidden md:flex w-full flex-col items-center py-3 text-[#6B7280] hover:text-[var(--color-accent)] transition-colors"
        >
          <Keyboard className="w-4 h-4" />
        </button>
        <button
          onClick={onToggleCollapsed}
          title="Collapse navigation"
          aria-label="Collapse navigation"
          aria-expanded="true"
          className="w-full flex flex-col items-center py-3 border-t border-[#1F2937] text-[#6B7280] hover:text-[var(--color-accent)] transition-colors"
        >
          <PanelLeftClose className="w-4 h-4" />
        </button>
      </div>
    </nav>
  );
}
